import { useId, useState, type ChangeEvent } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import styles from "./PaymentsFilters.module.css";
import { Button, Collapse, Input, Select } from "../../../shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import type { PaymentDirectionFilter } from "./applyPaymentQuery";

export type PaymentsFiltersProps = {
  headerText: string;
  searchValue: string;
  direction: PaymentDirectionFilter;
  minPayment: string;
  maxPayment: string;
  onSearchValueChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSearchClear: () => void;
  onSearchPressed: () => void;
  onDirectionChange: (value: PaymentDirectionFilter | undefined) => void;
  onMinPaymentChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onMaxPaymentChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onApply: () => void;
  onClear: () => void;
  hasAppliedOrDraftFilters: boolean;
};

const DIRECTION_OPTIONS = [
  { value: "both", label: AppStrings.BOTH },
  { value: "deposit", label: AppStrings.DEPOSIT },
  { value: "withdrawal", label: AppStrings.WITHDRAWAL },
];

const PaymentsFilters = ({
  headerText,
  searchValue,
  direction,
  minPayment,
  maxPayment,
  onSearchValueChange,
  onSearchClear,
  onSearchPressed,
  onDirectionChange,
  onMinPaymentChange,
  onMaxPaymentChange,
  onApply,
  onClear,
  hasAppliedOrDraftFilters,
}: PaymentsFiltersProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const panelId = useId();

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerRow}>
        <p className={styles.headerText}>{headerText}</p>

        <div className={styles.headerActions}>
          <Input
            variant="search"
            placeholder={AppStrings.PAYMENT_SEARCH_PAYMENTS}
            className={styles.searchInput}
            value={searchValue}
            onChange={(event) => {
              onSearchValueChange(event);
              if (event.type === "click") {
                onSearchClear();
              }
            }}
            allowClear
            data-testid="payments-search-input"
          />

          <Button
            size="large"
            onClick={onSearchPressed}
            data-testid="payments-search-button"
          >
            <SearchOutlined
              style={{
                fontSize: "20px",
                color: "var(--COLOR-LIGHT_TEXT_COLOR)",
              }}
            />
          </Button>

          <Button
            intent="primary"
            size="large"
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
            aria-controls={panelId}
            data-testid="payments-filters-toggle"
          >
            <span className={styles.filterToggleInner}>
              <Filter size={18} />
              {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          </Button>
        </div>
      </div>

      <Collapse open={open} className={styles.collapseHost}>
        <div
          id={panelId}
          className={styles.filtersPanel}
          data-testid="payments-filters-panel"
        >
          <Select
            allowClear
            className={styles.compactFilter}
            options={DIRECTION_OPTIONS}
            value={direction}
            onChange={onDirectionChange}
            placeholder={AppStrings.PAYMENT_DIRECTION_FILTER}
            virtual={false}
            data-testid="payments-direction-filter"
          />

          <Input
            className={styles.amountInput}
            type="number"
            min={0}
            step="0.01"
            value={minPayment}
            onChange={onMinPaymentChange}
            placeholder={AppStrings.PAYMENT_MIN_PAYMENT}
            data-testid="payments-min-filter"
          />

          <Input
            className={styles.amountInput}
            type="number"
            min={0}
            step="0.01"
            value={maxPayment}
            onChange={onMaxPaymentChange}
            placeholder={AppStrings.PAYMENT_MAX_PAYMENT}
            data-testid="payments-max-filter"
          />

          <Button
            size="large"
            onClick={onApply}
            data-testid="payments-apply-filters"
          >
            {AppStrings.APPLY}
          </Button>

          <Button
            variant="ghost"
            size="large"
            onClick={onClear}
            disabled={!hasAppliedOrDraftFilters}
            data-testid="payments-clear-filters"
          >
            {AppStrings.CLEAR_FILTERS}
          </Button>
        </div>
      </Collapse>
    </div>
  );
};

export default PaymentsFilters;
